/**
 * ESC/POS Command Builder
 * Converts receipt data to ESC/POS commands for thermal printer
 */

import dayjs from 'dayjs'
// eslint-disable-next-line import/no-extraneous-dependencies
import JsBarcode from 'jsbarcode'
import { isEmpty } from 'lodash-es'
import { currency, formatDate } from '../formatting'
import { PAYMENT_TYPE, paymentTypes } from '../static'
import { removeBase64UrlPrefix, isOptionEnabled } from '../helper'
import { getOjolDetailForReceipt } from './receiptOjolHelper'
import { getTableDetailForReceipt } from './receiptTableHelper'
import { getServedByNamesForReceipt } from './receiptServedByHelper'
import { getReceiptContact } from './receiptContactHelper'
import { getReceiptWitholding } from './receiptWitholdingHelper'

// ESC/POS Control Characters
const LF = '\x0A'

// Paper width configurations (in characters)
export const PAPER_WIDTHS = {
  WIDTH_80MM: 44, // 80mm thermal paper
  WIDTH_58MM: 32, // 58mm thermal paper = 32 characters
  WIDTH_DOTMATRIX: 40, // Dot matrix (mis. Epson TM-U220, ~76mm) — nilai aman-konservatif, mudah di-tune
  DEFAULT: 42, // Default/legacy width
}

const wrapWords = (text = '', width = 42) => {
  const t = String(text ?? '').trim()
  if (!t) {
    return ['']
  }

  const words = t.split(/\s+/)
  const lines = []
  let line = ''

  words.forEach((w) => {
    if (!line) {
      line = w
      return
    }

    if (`${line} ${w}`.length <= width) {
      line = `${line} ${w}`
      return
    }

    lines.push(line)
    line = w
  })

  if (line) {
    lines.push(line)
  }
  return lines
}

const pushWrappedText = (commands, text, width = 42, options = {}) => {
  const { prefix = '', indent = '' } = options

  const firstWidth = width - prefix.length

  const raw = String(text ?? '')
  const firstLines = wrapWords(raw, firstWidth)

  // baris pertama pakai prefix
  commands.push(prefix + (firstLines[0] ?? '') + LF)

  // baris berikutnya pakai indent
  for (let i = 1; i < firstLines.length; i += 1) {
    commands.push(indent + firstLines[i] + LF)
  }
}

/**
 * Get paper width based on paper size option
 */
const getPaperWidth = (paperSize) => {
  switch (paperSize) {
    case '80mm':
    case 80:
      return PAPER_WIDTHS.WIDTH_80MM
    case '58mm':
    case 58:
      return PAPER_WIDTHS.WIDTH_58MM
    case 'dotmatrix':
      return PAPER_WIDTHS.WIDTH_DOTMATRIX
    default:
      return PAPER_WIDTHS.DEFAULT
  }
}

// ESC/POS Commands
// Format sesuai dokumentasi QZ Tray: https://qz.io/docs/raw#escpos
export const ESCPOS = {
  // Initialize printer
  INIT: '\x1B\x40',

  // Text alignment
  ALIGN_LEFT: '\x1B\x61\x00',
  ALIGN_CENTER: '\x1B\x61\x01',
  ALIGN_RIGHT: '\x1B\x61\x02',

  // Text style
  BOLD_ON: '\x1B\x45\x01',
  BOLD_OFF: '\x1B\x45\x00',
  UNDERLINE_ON: '\x1B\x2D\x01',
  UNDERLINE_OFF: '\x1B\x2D\x00',
  DOUBLE_WIDTH_ON: '\x1D\x21\x10',
  DOUBLE_HEIGHT_ON: '\x1D\x21\x01',
  DOUBLE_SIZE_ON: '\x1D\x21\x11',
  NORMAL_SIZE: '\x1D\x21\x00',
  SMALL_TEXT: '\x1B\x4D\x31', // small text
  NORMAL_TEXT: '\x1B\x4D\x30', // normal text

  // Line feed
  LINE_FEED: '\x0A',
  FEED_LINES: (lines) => `\x1B\x64${String.fromCharCode(Math.max(0, Math.min(255, lines)))}`,

  // Paper cut
  // Old syntax: '\x1B' + '\x69' (legacy, masih digunakan beberapa printer)
  // New syntax: '\x1D' + '\x56' + '\x00' (full cut) atau '\x1D' + '\x56' + '\x01' (partial cut)
  CUT_PAPER: '\x1B\x69', // Old syntax untuk kompatibilitas
  CUT_PAPER_FULL: '\x1D\x56\x00', // New syntax - full cut (sesuai dokumentasi QZ Tray)
  CUT_PAPER_PARTIAL: '\x1D\x56\x01', // New syntax - partial cut

  // Cash drawer
  // ESC/POS command: ESC p m t1 t2
  // m = pin number (0 or 1), t1 = pulse duration (25ms), t2 = pulse interval (250ms)
  // Common variations:
  // - Pin 0: '\x1B\x70\x00\x19\xFA' (most common, used by Epson TM series, Star TSP series)
  // - Pin 1: '\x1B\x70\x01\x19\xFA' (some printers use pin 1)
  // - Alternative: '\x10\x14\x01\x00\x01' (some Chinese printers)
  OPEN_CASH_DRAWER: '\x1B\x70\x00\x19\xFA', // Open cash drawer (pin 0, 25ms pulse, 250ms interval) - most common in Indonesia
  OPEN_CASH_DRAWER_PIN1: '\x1B\x70\x01\x19\xFA', // Alternative: pin 1 (for some Star printers)

  // Helper function to generate cash drawer command with custom pin
  OPEN_CASH_DRAWER_CUSTOM: (pin = 0, pulseDuration = 25, pulseInterval = 250) => {
    // ESC p m t1 t2
    return `\x1B\x70${String.fromCharCode(pin)}${String.fromCharCode(
      pulseDuration,
    )}${String.fromCharCode(pulseInterval)}`
  },

  // Feed paper before content (prevents auto-cut on some printers)
  FEED_PAPER: '\x1B\x64\x03', // Feed 3 lines

  // Character spacing
  CHAR_SPACING: (n) => `\x1B\x20${String.fromCharCode(n)}`,
  CR: '\n',
}

/**
 * Format quantity (absolute value)
 */
const formatQty = (qty) => {
  return Math.abs(qty)
}

/**
 * Create separator line
 */
const createLine = (character = '=', width = 42) => {
  return character.repeat(width) + LF
}

/**
 * Create row with left and right aligned text
 * When content overflows, truncates the RIGHT side (value) to preserve the LEFT label.
 * Use maxRightLength option to explicitly limit right side chars (e.g. for long customer names).
 */
const createRow = (left, right, width = 42, options = {}) => {
  const { maxRightLength } = options
  const leftStr = String(left || '')
  let rightStr = String(right || '')

  // Batasi panjang sisi kanan jika melebihi ruang tersedia (label kiri tetap utuh)
  const availableForRight = width - leftStr.length - 1 // -1 untuk spasi
  const effectiveMaxRight =
    maxRightLength != null ? Math.min(maxRightLength, availableForRight) : availableForRight

  if (rightStr.length > effectiveMaxRight) {
    const ellipsis = '...'
    const truncateAt = Math.max(0, effectiveMaxRight - ellipsis.length)
    rightStr = rightStr.substring(0, truncateAt) + ellipsis
  }

  const spaces = width - leftStr.length - rightStr.length

  if (spaces >= 0) {
    return leftStr + ' '.repeat(spaces) + rightStr + LF
  }
  // Fallback: jika masih overflow (left label sangat panjang), truncate right
  const truncatedRight = rightStr.substring(0, Math.max(0, width - leftStr.length - 1))
  return `${leftStr} ${truncatedRight}${LF}`
}

/**
 * Seperti createRow, tapi nilai tidak pernah dipotong:
 * label kepanjangan di-wrap (indent dipertahankan), nilai rata kanan di baris berikutnya
 */
const createRowWrapped = (left, right, width = 42) => {
  const leftStr = String(left || '')
  const rightStr = String(right || '')
  if (leftStr.length + 1 + rightStr.length <= width) {
    return createRow(leftStr, rightStr, width)
  }
  const indent = leftStr.match(/^\s*/)[0]
  const labelLines = wrapWords(leftStr, width - indent.length).map((line) => indent + line)
  return `${labelLines.join(LF)}${LF}${createRow('', rightStr, width)}`
}

/**
 * Cetak baris ekstra (modifier) per item.
 * Menerima array modifiers langsung agar bisa dipakai loop item maupun labeling.
 * withPrice=true (struk/bill) → harga di kanan; selain itu (kitchen/checker/label) nama saja.
 */
const pushModifierLines = (commands, modifiers, width, options = {}) => {
  const { withPrice = false } = options
  const mods = Array.isArray(modifiers) ? modifiers : []
  mods.forEach((m) => {
    const mQty = Number(m.qty) || 1
    const label = `  + ${m.option_name || '-'}${mQty > 1 ? ` x${mQty}` : ''}`
    const price = Number(m.price) || 0
    if (withPrice && price > 0) {
      // Harga per-unit-item (rekonsiliasi dengan baris harga satuan, bukan amount yang dikali qty item)
      commands.push(createRow(label, currency(price * mQty), width))
    } else {
      pushWrappedText(commands, label, width, { indent: '    ' })
    }
  })
}

const getQrCodeData = (qr, qrModuleSize = 4) => {
  // Validate QR string
  if (!qr || typeof qr !== 'string' || qr.length === 0) {
    console.warn('Invalid QR string provided to getQrCodeData')
    return []
  }

  // The dot size of the QR code (1-16). Lebih kecil = QR lebih kecil.
  const dots = String.fromCharCode(Math.min(16, Math.max(1, Number(qrModuleSize) || 4)))

  // Calculate length for Function 080: pL + pH × 256 = qr.length + 3
  // According to ESC/POS spec: k = (pL + pH × 256) − 3, where k is data length
  // So pL + pH × 256 = k + 3 = qr.length + 3
  // The +3 accounts for: cn (1 byte) + fn (1 byte) + m (1 byte) = 3 bytes
  const dataLength = qr.length
  const totalLength = dataLength + 3
  const pL = chr(totalLength % 256)
  const pH = chr(Math.floor(totalLength / 256))

  // Build Function 080 command: GS ( k pL pH cn fn m d1...dk
  // Format: \x1D\x28\x6B pL pH \x31\x50\x30 [qr data]
  // Where:
  //   \x1D\x28\x6B = GS ( k (command prefix)
  //   pL, pH = length bytes (low byte, high byte) - must be binary bytes, not ASCII
  //   \x31 = cn = 49 decimal = QR Code function indicator
  //   \x50 = fn = 80 decimal = Function 080 (store QR code data)
  //   \x30 = m = 48 decimal = Mode parameter
  //   [qr data] = the actual QR code string data
  //
  // IMPORTANT: Ensure pL and pH are sent as binary bytes, not ASCII characters
  // Using String.fromCharCode ensures they are binary bytes
  const function080Command = `\x1D\x28\x6B${pL}${pH}\x31\x50\x30${qr}`

  return [
    `\x1D\x28\x6B\x04\x00\x31\x41\x32\x00`, // <Function 165> select the model (model 2 is widely supported)
    `\x1D\x28\x6B\x03\x00\x31\x43${dots}`, // <Function 167> set the size of the module
    `\x1D\x28\x6B\x03\x00\x31\x45\x30`, // <Function 169> select level of error correction (48,49,50,51) printer-dependent
    function080Command, // <Function 080> store QR code data in symbol storage area
    `\x1D\x28\x6B\x03\x00\x31\x51\x30`, // <Function 081> print the symbol data in the symbol storage area
    `\x1D\x28\x6B\x03\x00\x31\x52\x30`, // Additional command for some printers
  ]
}

const chr = (n) => String.fromCharCode(n)

const getBarcodeModuleWidth = (payloadLength) => {
  // Barcode alfanumerik panjang gampang melewati lebar printable area.
  // Pakai module width lebih kecil agar ref_number seperti "211/SPR/INV/MAR/09/-/616" tetap muat.
  if (payloadLength >= 12) {
    return 1
  }
  return 2
}

const sanitizeCode128Value = (code128) => {
  const normalized = String(code128 ?? '').trim()
  if (!normalized) {
    return ''
  }

  // Batasi ke printable ASCII agar aman untuk generator barcode dan fallback ESC/POS.
  return normalized.replace(/[^\x20-\x7E]/g, '')
}

const getBarcodeImageMaxWidth = (paperWidth) => {
  if (paperWidth <= PAPER_WIDTHS.WIDTH_58MM) {
    return 320
  }
  if (paperWidth >= PAPER_WIDTHS.WIDTH_80MM) {
    return 520
  }
  return 460
}

const getCode128ImageData = (code128, paperWidth = PAPER_WIDTHS.DEFAULT) => {
  const sanitized = sanitizeCode128Value(code128)
  if (!sanitized) {
    return null
  }
  if (typeof document === 'undefined') {
    return null
  }

  try {
    const canvas = document.createElement('canvas')
    JsBarcode(canvas, sanitized, {
      format: 'CODE128',
      displayValue: false,
      margin: 0,
      width: 1,
      height: 48,
      background: '#FFFFFF',
      lineColor: '#000000',
    })

    if (!canvas.width || !canvas.height) {
      return null
    }

    const maxWidth = getBarcodeImageMaxWidth(paperWidth)
    let outputCanvas = canvas

    if (canvas.width > maxWidth) {
      const scale = maxWidth / canvas.width
      const resizedCanvas = document.createElement('canvas')
      resizedCanvas.width = Math.max(1, Math.floor(canvas.width * scale))
      resizedCanvas.height = Math.max(1, Math.floor(canvas.height * scale))

      const context = resizedCanvas.getContext('2d')
      if (!context) {
        return null
      }

      context.fillStyle = '#FFFFFF'
      context.fillRect(0, 0, resizedCanvas.width, resizedCanvas.height)
      context.imageSmoothingEnabled = false
      context.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height)
      outputCanvas = resizedCanvas
    }

    const imageBase64 = removeBase64UrlPrefix(outputCanvas.toDataURL('image/png'))
    if (!imageBase64) {
      return null
    }

    return {
      type: 'raw',
      format: 'image',
      flavor: 'base64',
      data: imageBase64,
      options: {
        language: 'ESCPOS',
        dotDensity: 'double',
      },
    }
  } catch (error) {
    console.warn('Failed to build Code128 barcode image', error)
    return null
  }
}

const getCode128Data = (code128) => {
  const sanitized = sanitizeCode128Value(code128)
  if (!sanitized) {
    return ''
  }

  const payload = `{B${sanitized}`
  const moduleWidth = getBarcodeModuleWidth(payload.length)

  return `\x1Dw${chr(moduleWidth)}\x1Dh${chr(48)}\x1Df${chr(0)}\x1DH${chr(0)}\x1Dk${chr(
    73,
  )}${chr(payload.length)}${payload}`
}

/**
 * Build ESC/POS commands for receipt
 * Sesuai dengan format di ReceiptContent.jsx
 */
const getUnitName = (units, item) => {
  if (!Array.isArray(units) || units.length === 0) {
    return ''
  }
  const unitId = item.unit_id ?? item.product?.unit_id
  const unit = units.find((u) => String(u.id) === String(unitId))
  return unit?.name ?? ''
}

export const buildReceiptCommands = (data, options = {}, formatMessage) => {
  const {
    printerFunction = 'struk',
    outlet = {},
    companyOptions = {},
    outletOptions = {},
    paperWidth, // '80mm', '58mm', or number (default: 42)
    enableCut = true, // Enable paper cut at the end (default: true)
    enableOpenCashDrawer = false, // Enable cash drawer open (default: false)
    cashDrawerPin = 0, // Cash drawer pin (0 or 1, default: 0) - most printers use pin 0
    qrModuleSize = 4,
    units = [], // Daftar satuan dari redux (unitsSelector) untuk menampilkan nama satuan di struk
  } = options

  // Get paper width (default: 42, or from paperWidth option)
  const width = paperWidth ? getPaperWidth(paperWidth) : PAPER_WIDTHS.DEFAULT

  // Printer dot matrix tak bisa render gambar raster (logo) → skip logo agar tidak jadi karakter sampah
  const isDotMatrix = paperWidth === 'dotmatrix'

  const commands = [ESCPOS.INIT]

  // Open cash drawer if enabled (before other commands)
  if (enableOpenCashDrawer) {
    // Use custom pin if specified, otherwise use default (pin 0)
    const cashDrawerCommand =
      cashDrawerPin === 1 ? ESCPOS.OPEN_CASH_DRAWER_PIN1 : ESCPOS.OPEN_CASH_DRAWER
    commands.push(cashDrawerCommand)
  }

  commands.push(ESCPOS.ALIGN_LEFT)
  if (printerFunction === 'struk' || printerFunction === 'bill') {
    commands.push(ESCPOS.ALIGN_CENTER)
    // Logo
    if (isOptionEnabled(outletOptions.print_style_show_logo) && !isDotMatrix) {
      let LOGO_BASE64 =
        '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'
      if (!isEmpty(outletOptions.print_style_logo_base64)) {
        LOGO_BASE64 = removeBase64UrlPrefix(outletOptions.print_style_logo_base64)
      }
      commands.push(
        {
          type: 'raw',
          format: 'image',
          flavor: 'base64',
          data: LOGO_BASE64,
          options: {
            language: 'ESCPOS',
            dotDensity: 'double',
          },
        },
        LF,
      )
    }

    // Header
    if (
      isOptionEnabled(outletOptions?.print_style_show_header) &&
      !isEmpty(outletOptions.print_style_header)
    ) {
      commands.push(outletOptions.print_style_header + LF)
    }
    // Outlet name
    if (isOptionEnabled(outletOptions?.print_style_show_outlet_name) && !isEmpty(outlet?.name)) {
      commands.push(ESCPOS.BOLD_ON)
      commands.push(outlet.name + LF)
      commands.push(ESCPOS.BOLD_OFF)
    }
    // Company name
    if (
      isOptionEnabled(outletOptions?.print_style_show_company_name) &&
      !isEmpty(companyOptions?.property_name)
    ) {
      commands.push(companyOptions.property_name + LF)
    }
    const receiptContact = getReceiptContact(outlet, companyOptions)
    // Alamat (prioritas outlet, fallback perusahaan)
    if (
      isOptionEnabled(outletOptions?.print_style_show_address) &&
      !isEmpty(receiptContact.address)
    ) {
      pushWrappedText(commands, receiptContact.address, width)
    }
    // Email
    if (
      isOptionEnabled(outletOptions?.print_style_show_email) &&
      !isEmpty(companyOptions?.property_email)
    ) {
      commands.push(companyOptions.property_email + LF)
    }
    // Telepon (prioritas outlet, fallback perusahaan)
    if (isOptionEnabled(outletOptions?.print_style_show_phone) && !isEmpty(receiptContact.phone)) {
      commands.push(receiptContact.phone + LF)
    }
  } else if (printerFunction === 'checker') {
    commands.push(ESCPOS.ALIGN_CENTER)
    commands.push(ESCPOS.BOLD_ON)
    commands.push(`CHECKER${LF}`)
    commands.push(ESCPOS.BOLD_OFF)
  } else if (printerFunction === 'kitchen') {
    commands.push(ESCPOS.ALIGN_CENTER)
    commands.push(ESCPOS.BOLD_ON)
    commands.push(`${data.title || formatMessage({ id: 'kitchen' })}${LF}`)
    commands.push(ESCPOS.BOLD_OFF)
  }
  commands.push(LF)

  commands.push(ESCPOS.ALIGN_LEFT, createLine('-', width))
  if (data.queue_number_formatted) {
    commands.push(
      createRow(formatMessage({ id: 'queue_number' }), data.queue_number_formatted, width),
    )
  }
  if (isOptionEnabled(outletOptions.print_style_show_order_number)) {
    commands.push(createRow(formatMessage({ id: 'order_number' }), data.ref_number || '-', width))
  }
  commands.push(
    createRow(
      formatMessage({ id: 'time' }),
      formatDate(data.log?.action?.created_at, 'DD MMMM YYYY HH:mm'),
      width,
    ),
    createRow(formatMessage({ id: 'report.cashier' }), data.log?.action?.user?.name || '-', width),
  )
  if (isOptionEnabled(outletOptions.print_style_show_customer)) {
    commands.push(
      createRow(formatMessage({ id: 'financeOrder.customer' }), data.contact?.name || '-', width, {
        maxRightLength: 25,
      }),
    )
  }
  if (outletOptions.print_style_show_order_type === '1') {
    commands.push(
      createRow(formatMessage({ id: 'report.order_type' }), data.invoice_type?.name || '-', width),
    )
  }

  const ojolDetail = getOjolDetailForReceipt(data)
  if (ojolDetail) {
    const providerLabel = ojolDetail.provider_name || ojolDetail.courier
    if (providerLabel) {
      commands.push(
        createRow(formatMessage({ id: 'cashier.receipt_ojol_name' }), providerLabel, width, {
          maxRightLength: 22,
        }),
      )
    }
    if (ojolDetail.order_name) {
      commands.push(
        createRow(
          formatMessage({ id: 'cashier.receipt_ojol_order_no' }),
          String(ojolDetail.order_name),
          width,
          { maxRightLength: 22 },
        ),
      )
    }
  }

  // Pegawai "Dilayani Oleh" — ikut aturan show/hide jenis transaksi
  const servedByNames = getServedByNamesForReceipt(data)
  if (servedByNames && outletOptions.print_style_show_order_type === '1') {
    commands.push(
      createRow(formatMessage({ id: 'cashier.receipt_served_by' }), servedByNames, width, {
        maxRightLength: 22,
      }),
    )
  }

  // Detail meja — ikut aturan show/hide jenis transaksi (print_style_show_order_type)
  const tableDetail = getTableDetailForReceipt(data)
  if (tableDetail && outletOptions.print_style_show_order_type === '1') {
    const tableName =
      tableDetail.table_name ||
      formatMessage({ id: 'cashier.table_detail.table_fallback' }, { id: tableDetail.table_id })
    commands.push(
      createRow(formatMessage({ id: 'cashier.receipt_table' }), tableName, width, {
        maxRightLength: 22,
      }),
    )
    if (tableDetail.order_name) {
      commands.push(
        createRow(
          formatMessage({ id: 'cashier.receipt_table_order_name' }),
          String(tableDetail.order_name),
          width,
          { maxRightLength: 22 },
        ),
      )
    }
    if (tableDetail.reserved_count) {
      commands.push(
        createRow(
          formatMessage({ id: 'cashier.receipt_table_guests' }),
          String(tableDetail.reserved_count),
          width,
        ),
      )
    }
  }

  commands.push(createLine('-', width))

  // Bawaan aktif: hanya '0' yang menyembunyikan deskripsi item
  const showItemDescription = outletOptions.print_style_show_product_description?.toString() !== '0'

  const witholding = getReceiptWitholding(
    data,
    outletOptions,
    formatMessage({ id: 'financeInvoices.witholding' }),
  )

  if (printerFunction === 'struk' || printerFunction === 'bill') {
    const hideProductLinePrices = outletOptions.print_style_show_product_price?.toString() === '0'
    const itemsLength = data.items?.length || 0
    // Struk/Bill format - with prices (or qty only when print_style_show_product_price is off)
    data.items?.forEach((item) => {
      // Product name (left aligned)
      pushWrappedText(commands, item.custom_product_name || item.product?.name || '-', width)
      const unitName = getUnitName(units, item)
      if (hideProductLinePrices) {
        const qtyLine = unitName ? `${formatQty(item.qty)} ${unitName}` : `${formatQty(item.qty)}x`
        commands.push(qtyLine + LF)
      } else {
        // Qty [satuan] x Price = Amount
        const qtyPrice = unitName
          ? `${formatQty(item.qty)} ${unitName} x ${currency(item.price)}`
          : `${formatQty(item.qty)}x ${currency(item.price)}`
        const amount = currency(item.amount || 0)
        commands.push(createRow(qtyPrice, amount, width))

        // Discount if any
        if (item.discount_amount > 0) {
          // Baris hadiah BOGO: tampilkan "Gratis - {nama promo}", bukan "Diskon per item"
          const isReward = Number(item.price_rule_reward_id) > 0
          const rewardName = item.price_rule?.name || item.price_rule_reward?.rule_name || ''
          let discountLabel
          if (isReward) {
            discountLabel = rewardName
              ? `${formatMessage({ id: 'priceRule.free' })} - ${rewardName}`
              : formatMessage({ id: 'priceRule.free' })
          } else {
            discountLabel =
              item.price_rule?.name ||
              formatMessage({ id: 'financePurchaseOrders.discount_per_item' })
          }
          const discountAmount = `(${currency(item.discount_amount)})`
          commands.push(createRow(discountLabel, discountAmount, width))
        }
      }

      // Ekstra (modifier) per item — harga di kanan kecuali harga produk disembunyikan
      pushModifierLines(commands, item.modifiers, width, { withPrice: !hideProductLinePrices })

      // Description/Note
      const lineDesc = item.desc ?? item.description
      if (showItemDescription && lineDesc) {
        pushWrappedText(commands, lineDesc, width, { prefix: '>' })
      }
      if (itemsLength > 1) {
        commands.push(LF)
      }
    })
    // commands.push(createLine('=', width))
    commands.push(createLine('-', width))

    // Subtotal
    const subtotalLabel = `Subtotal ${data.items?.length || 0} produk`
    commands.push(createRow(subtotalLabel, currency(data.amount || 0), width))

    // Discount per item
    if (data.discount_per_item > 0) {
      commands.push(
        createRow(
          formatMessage({ id: 'financePurchaseOrders.discount_per_item' }),
          `(${currency(data.discount_per_item)})`,
          width,
        ),
      )
    }

    // Additional discounts (array)
    if (!isEmpty(data.additional_discounts) && Array.isArray(data.additional_discounts)) {
      data.additional_discounts.forEach((discount) => {
        const percentLabel =
          discount.discount_type === 1 // PERCENT
            ? ` (${parseFloat(discount.additional_discount_percent || 0)}%)`
            : ''
        const discountLabel =
          formatMessage({ id: 'financePurchaseOrders.additional_discount' }) + percentLabel
        commands.push(
          createRow(
            discountLabel,
            `(${currency(discount.additional_discount_amount || 0)})`,
            width,
          ),
        )
      })
    }

    // Tax
    if (!isEmpty(data.taxes)) {
      Object.keys(data.taxes).forEach((key) => {
        const taxLabel = `${key} (${data.taxes[key].percent}%)`
        const { value } = data.taxes[key]
        const _value = value > 0 ? currency(value) : `(${currency(Math.abs(value))})`
        commands.push(createRow(taxLabel, _value, width))
      })
    }

    // Biaya pengiriman shipping cost
    if (data.shipping_cost > 0) {
      commands.push(
        createRow(
          formatMessage({ id: 'shipping.shipping_cost' }),
          currency(data.shipping_cost),
          width,
        ),
      )
    }

    // Fees
    if (!isEmpty(data.fees) && Array.isArray(data.fees)) {
      data.fees.forEach((fee) => {
        commands.push(createRow(fee.name || '-', currency(fee.value || 0), width))
      })
    }

    // Additional fees
    if (data.totalMdrFee > 0) {
      commands.push(
        createRow(formatMessage({ id: 'additional_fee' }), currency(data.totalMdrFee), width),
      )
    }

    // Pemotongan (bisa disembunyikan per outlet)
    witholding.rows.forEach((row) => {
      commands.push(createRow(row.label, currency(row.amount), width))
    })

    // Total
    commands.push(createRow('Total', currency(witholding.amountAfterTax), width))
  } else if (printerFunction === 'checker' || printerFunction === 'kitchen') {
    commands.push(ESCPOS.ALIGN_LEFT)
    // Kitchen/Checker format - no prices, qty [satuan] in front
    data.items?.forEach((item) => {
      const unitName = getUnitName(units, item)
      const qtyStr = unitName ? `${formatQty(item.qty)} ${unitName}` : formatQty(item.qty)
      pushWrappedText(
        commands,
        `(${qtyStr}) ${item.custom_product_name || item.product?.name}`,
        width,
      )
      // Ekstra (modifier) per item — tanpa harga (untuk dapur/checker)
      pushModifierLines(commands, item.modifiers, width, { withPrice: false })
      // Description/Note
      const lineDesc = item.desc ?? item.description
      if (showItemDescription && lineDesc) {
        pushWrappedText(commands, lineDesc, width, { prefix: '>' })
        commands.push(LF)
      }
    })
    commands.push(LF)
  }

  if (printerFunction === 'struk') {
    commands.push(ESCPOS.ALIGN_LEFT)
    commands.push(createLine('-', width))
    data.payments?.forEach((payment, index) => {
      let amountAfterTax = payment.amount_after_tax
      const payback = payment.payback || 0
      if (payback > 0) {
        amountAfterTax = payment.amount_after_tax + payback
      }
      amountAfterTax = witholding.getPaymentAmount(amountAfterTax, index, data.payments.length)
      commands.push(
        createRow(
          `${payment.payment_type?.name || '-'} ${
            (payment.payment_fee || 0) > 0 ? `+ ${formatMessage({ id: 'additional_fee' })}` : ''
          }`,
          currency(amountAfterTax),
          width,
        ),
      )
    })
    if (data.payment_amount > 0) {
      commands.push(
        createRow(
          formatMessage({ id: 'report.total_payment' }),
          currency((Number(data.payment_amount) || 0) + witholding.addBack),
          width,
        ),
      )
    }
    if (data.payment_back_amount > 0) {
      commands.push(
        createRow(
          formatMessage({ id: 'report.return' }),
          currency(data.payment_back_amount),
          width,
        ),
      )
    }
    commands.push(createLine('-', width))
  }

  // ========================================
  // FOOTER SECTION
  // ========================================
  if (printerFunction === 'struk' || printerFunction === 'bill') {
    // BARCODE
    if (isOptionEnabled(outletOptions.print_style_show_barcode)) {
      commands.push(ESCPOS.ALIGN_CENTER)
      const barcodeValue = data.ref_number || data.id
      const barcodeImageData = getCode128ImageData(`${barcodeValue}`, width)
      if (barcodeImageData) {
        commands.push(LF)
        commands.push(barcodeImageData)
        commands.push(LF)
      } else {
        const code128Data = getCode128Data(`${barcodeValue}`)
        if (code128Data) {
          commands.push(LF)
          commands.push(code128Data)
          commands.push(LF)
        }
      }
    }

    // QRCODE
    if (isOptionEnabled(outletOptions.print_style_show_qrcode)) {
      const strukUrl = outletOptions.print_style_qrcode_text || data.invoice_url
      if (strukUrl) {
        commands.push(ESCPOS.ALIGN_CENTER)
        const qrData = getQrCodeData(strukUrl, qrModuleSize)
        if (!isOptionEnabled(outletOptions.print_style_show_barcode)) {
          commands.push(LF)
        }
        commands.push(...qrData)
        commands.push(LF)
      }
    }
    if (
      isOptionEnabled(outletOptions.print_style_show_qrcode) ||
      isOptionEnabled(outletOptions.print_style_show_barcode)
    ) {
      commands.push(createLine('-', width))
    }

    // Note
    if (isOptionEnabled(outletOptions.print_style_show_note) && outletOptions.print_style_note) {
      commands.push(ESCPOS.ALIGN_CENTER, ESCPOS.SMALL_TEXT)
      pushWrappedText(commands, outletOptions.print_style_note, width)
      commands.push(ESCPOS.NORMAL_TEXT)
    }

    // Footer
    if (
      isOptionEnabled(outletOptions.print_style_show_footer) &&
      outletOptions.print_style_footer
    ) {
      commands.push(ESCPOS.ALIGN_CENTER)
      pushWrappedText(commands, outletOptions.print_style_footer, width)
    }

    // Social media
    // if (outletOptions.print_style_show_social_media === '1') {
    //   commands.push(ESCPOS.ALIGN_CENTER)
    //   commands.push(`Instagram: Uplines\n`)
    //   commands.push(LF)
    // }
  }

  // Jumlah cetak struk (setting lokal aktif) — optimistic: cetakan saat ini = count BE + 1
  if (options.printReceiptCountActive && options.printerFunction === 'struk') {
    const printedNow = (Number(data.print_receipt_count) || 0) + 1
    commands.push(ESCPOS.ALIGN_CENTER)
    commands.push(`Dicetak ${printedNow} kali${LF}`)
  }

  // Timestamp
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(`--- ${formatDate(dayjs())} ---${LF}`)

  commands.push(ESCPOS.FEED_LINES(8))
  if (enableCut) {
    commands.push(ESCPOS.CUT_PAPER_FULL)
  }

  return commands
}

/**
 * Build ESC/POS commands for Web Order QR code receipt
 * Format: Title (center), QR Code, URL (center), Powered By
 */
export const buildWebOrderCommands = (webOrderData, options = {}, formatMessage) => {
  const {
    outlet = {},
    companyOptions = {},
    outletOptions = {},
    paperWidth, // '80mm', '58mm', or number (default: 42)
    enableCut = true, // Enable paper cut at the end (default: true)
    // Note: Web Order tidak perlu cash drawer karena hanya untuk menampilkan QR code menu
    qrModuleSize = 6, // QR code size (default: 6 for better visibility)
  } = options

  // Get paper width (default: 42, or from paperWidth option)
  const width = paperWidth ? getPaperWidth(paperWidth) : PAPER_WIDTHS.DEFAULT

  const commands = [ESCPOS.INIT, ESCPOS.ALIGN_CENTER]

  // Outlet name (optional)
  if (isOptionEnabled(outletOptions?.print_style_show_outlet_name) && !isEmpty(outlet?.name)) {
    commands.push(outlet.name + LF)
  }

  // Company name (optional)
  if (
    isOptionEnabled(outletOptions?.print_style_show_company_name) &&
    !isEmpty(companyOptions?.property_name)
  ) {
    commands.push(companyOptions.property_name + LF)
  }

  commands.push(LF)
  commands.push(createLine('-', width))

  // Title (center aligned)
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(ESCPOS.BOLD_ON)
  if (formatMessage) {
    commands.push(
      `${
        formatMessage({ id: 'scan_qrcode_to_view_the_menu' }) || 'Pindai QR Code untuk melihat menu'
      }${LF}`,
    )
  } else {
    commands.push(`Pindai QR Code untuk melihat menu${LF}`)
  }
  commands.push(ESCPOS.BOLD_OFF)
  commands.push(LF)

  // QR Code (center aligned)
  if (webOrderData.web_order_url) {
    commands.push(LF)
    const qrData = getQrCodeData(webOrderData.web_order_url, qrModuleSize)
    commands.push(...qrData)
    commands.push(LF)
    commands.push(LF)
  }

  // URL (center aligned, small text)
  if (webOrderData.web_order_url) {
    commands.push(ESCPOS.ALIGN_CENTER)
    commands.push(ESCPOS.SMALL_TEXT)
    // Split URL if too long (wrap text)
    const url = webOrderData.web_order_url
    const maxLength = width - 4 // Leave some margin
    if (url.length > maxLength) {
      // Split URL into multiple lines
      let remainingUrl = url
      while (remainingUrl.length > 0) {
        const line = remainingUrl.substring(0, maxLength)
        remainingUrl = remainingUrl.substring(maxLength)
        commands.push(line + LF)
      }
    } else {
      commands.push(url + LF)
    }
    commands.push(ESCPOS.NORMAL_TEXT)
    commands.push(LF)
  }

  // Powered By section
  commands.push(LF)
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(ESCPOS.BOLD_ON)
  commands.push(`Powered By${LF}`)
  commands.push(ESCPOS.BOLD_OFF)
  // Note: Logo printing via ESC/POS image command would require base64 conversion
  // For now, just print text
  commands.push(`Kledo${LF}`)
  commands.push(LF)

  // Timestamp
  commands.push(createLine('-', width))
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(`--- ${formatDate(dayjs())} ---${LF}`)

  // Feed lines before cut
  commands.push(ESCPOS.FEED_LINES(8))

  if (enableCut) {
    commands.push(ESCPOS.CUT_PAPER_FULL)
  }

  return commands
}

/**
 * Build ESC/POS commands for QRIS payment receipt
 * Format: Order ID (center), QR Code, Total Payment (center)
 */
export const buildQRISCommands = (qrisData, options = {}, formatMessage) => {
  const {
    outlet = {},
    companyOptions = {},
    outletOptions = {},
    paperWidth, // '80mm', '58mm', or number (default: 42)
    enableCut = true, // Enable paper cut at the end (default: true)
    // Note: QRIS tidak perlu cash drawer karena pembayaran digital
    qrModuleSize = 6, // QR code size (default: 6 for better visibility)
  } = options

  // Get paper width (default: 42, or from paperWidth option)
  const width = paperWidth ? getPaperWidth(paperWidth) : PAPER_WIDTHS.DEFAULT

  const commands = [ESCPOS.INIT, ESCPOS.ALIGN_CENTER]

  // Outlet name (optional)
  if (isOptionEnabled(outletOptions?.print_style_show_outlet_name) && !isEmpty(outlet?.name)) {
    commands.push(outlet.name + LF)
  }

  // Company name (optional)
  if (
    isOptionEnabled(outletOptions?.print_style_show_company_name) &&
    !isEmpty(companyOptions?.property_name)
  ) {
    commands.push(companyOptions.property_name + LF)
  }

  commands.push(LF)
  commands.push(createLine('-', width))

  // Order ID (center aligned)
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(ESCPOS.BOLD_ON)
  commands.push(`Order ID #${qrisData.ref_number || '-'}${LF}`)
  commands.push(ESCPOS.BOLD_OFF)
  commands.push(LF)

  // QR Code (center aligned)
  if (qrisData.qr_string) {
    // Validate and clean QR string
    const qrString = String(qrisData.qr_string).trim()
    if (qrString && qrString.length > 0) {
      commands.push(LF)
      const qrData = getQrCodeData(qrString, qrModuleSize)
      commands.push(...qrData)
      commands.push(LF)
      commands.push(LF)
    } else {
      console.warn('QRIS qr_string is empty or invalid')
    }
  }

  // Total Payment (center aligned)
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(ESCPOS.BOLD_ON)
  commands.push(`Total Yang Harus Dibayar${LF}`)
  commands.push(ESCPOS.BOLD_OFF)
  commands.push(currency(qrisData.amount_after_tax || 0) + LF)
  commands.push(LF)

  // Expiry time (optional, if available)
  if (qrisData.expiry_time) {
    commands.push(ESCPOS.SMALL_TEXT)
    if (formatMessage) {
      commands.push(
        `${formatMessage({ id: 'expires_at' }) || 'Berlaku hingga'}: ${qrisData.expiry_time}${LF}`,
      )
    } else {
      commands.push(`Berlaku hingga: ${qrisData.expiry_time}${LF}`)
    }
    commands.push(ESCPOS.NORMAL_TEXT)
    commands.push(LF)
  }

  // Timestamp
  commands.push(createLine('-', width))
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(`--- ${formatDate(dayjs())} ---${LF}`)

  // Feed lines before cut
  commands.push(ESCPOS.FEED_LINES(8))
  if (enableCut) {
    commands.push(ESCPOS.CUT_PAPER_FULL)
  }

  return commands
}

/**
 * Convert commands array to string
 */
export const commandsToString = (commands) => {
  return commands.join('')
}

/**
 * Convert commands array to buffer (for some printers)
 */
export const commandsToBuffer = (commands) => {
  const str = commandsToString(commands)
  return Buffer.from(str, 'binary')
}

/**
 * Build ESC/POS commands for Shift Detail receipt
 * Layout selaras dengan halaman detail shift (4 section + Produk Terjual):
 *  1. Ringkasan Penjualan
 *  2. Metode Pembayaran (+ breakdown cashless jika withPaymentDetail)
 *  3. Pergerakan Kas (Uang Fisik)
 *  4. Rekonsiliasi Kas
 *  5. Produk Terjual (opsional, jika withProduct)
 */
export const buildShiftDetailCommands = (shiftData = {}, options = {}, formatMessage) => {
  const {
    outlet = {},
    companyOptions = {},
    outletOptions = {},
    paperWidth,
    enableCut = true,
    withProduct = false,
    withPaymentDetail = true,
  } = options

  // Bawaan aktif: hanya '0' yang menyembunyikan deskripsi produk
  const showItemDescription = outletOptions.print_style_show_product_description?.toString() !== '0'

  const width = paperWidth ? getPaperWidth(paperWidth) : PAPER_WIDTHS.DEFAULT
  const border = createLine('-', width)
  const doubleBorder = createLine('=', width)

  const commands = [ESCPOS.INIT, ESCPOS.ALIGN_CENTER]

  // ===== Header =====
  if (!isEmpty(outlet?.name)) {
    commands.push(outlet.name + LF)
  }
  const receiptPhone = getReceiptContact(outlet, companyOptions).phone
  if (!isEmpty(receiptPhone)) {
    commands.push(receiptPhone + LF)
  }
  if (!isEmpty(companyOptions?.property_name)) {
    commands.push(companyOptions.property_name + LF)
  }

  commands.push(LF)
  commands.push(doubleBorder)
  commands.push(ESCPOS.BOLD_ON)
  commands.push(`${formatMessage({ id: 'shift_report' })}${LF}`)
  commands.push(ESCPOS.BOLD_OFF)
  commands.push(doubleBorder)

  // ===== Kasir & Waktu =====
  commands.push(ESCPOS.ALIGN_LEFT)
  commands.push(
    createRowWrapped(formatMessage({ id: 'report.cashier' }), shiftData.user?.name || '-', width),
    createRowWrapped(
      formatMessage({ id: 'report.open_time' }),
      shiftData.start_time ? formatDate(shiftData.start_time, 'DD MMM YYYY HH:mm') : '-',
      width,
    ),
    createRowWrapped(
      formatMessage({ id: 'report.close_time' }),
      shiftData.end_time ? formatDate(shiftData.end_time, 'DD MMM YYYY HH:mm') : '-',
      width,
    ),
  )

  // ===== Data preparation =====
  const payments = Array.isArray(shiftData.payments) ? shiftData.payments : []
  const items = Array.isArray(shiftData.items) ? shiftData.items : []

  const cashPayment = Number(shiftData.cash_payment) || 0
  const cashRefund = Number(shiftData.cash_refund) || 0
  const cashlessPayment = Number(shiftData.cashless_payment) || 0
  const edcTotal = Number(shiftData.edc_payment) || 0
  const transferTotal = Number(shiftData.transfer_payment) || 0
  const depositTotal = Number(shiftData.deposit_payment) || 0
  const openingBalance = Number(shiftData.opening_balance) || 0
  const cashIn = Number(shiftData.cash_in) || 0
  const cashOut = Math.abs(Number(shiftData.cash_out) || 0)
  const cashSales = Number(shiftData.cash_sales) || 0
  const closingBalance = Number(shiftData.closing_balance) || 0
  const discountTotal = Number(shiftData.discount_total) || 0
  const taxAmount = Number(shiftData.tax_amount) || 0
  const hasNonCash = edcTotal > 0 || transferTotal > 0 || depositTotal > 0

  const totalProductGross = items.reduce((sum, i) => sum + (Number(i?.amount) || 0), 0)
  const totalProductQty = items.reduce((sum, i) => sum + (Number(i?.qty) || 0), 0)
  const totalOmzet = totalProductGross - discountTotal
  const totalPayment = cashPayment + cashlessPayment
  const selisihKas = closingBalance - cashSales
  let selisihStatusId = 'report.status_matched'
  if (selisihKas > 0) {
    selisihStatusId = 'report.status_more'
  } else if (selisihKas < 0) {
    selisihStatusId = 'report.status_less'
  }

  const matchType = (paymentTypeId, mobileId, webUuid) =>
    paymentTypeId === mobileId || paymentTypeId === webUuid

  const sectionTitle = (number, key) => {
    commands.push(border)
    commands.push(ESCPOS.BOLD_ON)
    commands.push(`${number}. ${formatMessage({ id: key }).toUpperCase()}${LF}`)
    commands.push(ESCPOS.BOLD_OFF)
    commands.push(border)
  }

  // ===== 1. Ringkasan Penjualan =====
  sectionTitle(1, 'report.sales_summary')
  commands.push(
    createRowWrapped(formatMessage({ id: 'report.total_omzet' }), currency(totalOmzet), width),
    createRowWrapped(
      formatMessage({ id: 'financePurchaseOrders.total_discount' }),
      currency(discountTotal),
      width,
    ),
    createRowWrapped(
      formatMessage({ id: 'report.sales_transaction_completed' }),
      `${shiftData.sales_transaction || 0}`,
      width,
    ),
    createRowWrapped(
      formatMessage({ id: 'report.pos_unpaid' }),
      `${shiftData.order_transaction || 0}`,
      width,
    ),
    createRowWrapped(
      formatMessage({ id: 'report.pos_unpaid_currency' }),
      currency(shiftData.order_amount || 0),
      width,
    ),
  )
  if (taxAmount > 0) {
    commands.push(
      createRowWrapped(formatMessage({ id: 'roles.reports_taxes' }), currency(taxAmount), width),
    )
  }

  // ===== 2. Metode Pembayaran =====
  sectionTitle(2, 'report.payment_method')
  commands.push(
    createRowWrapped(formatMessage({ id: 'report.cash_payment' }), currency(cashPayment), width),
  )

  if (hasNonCash) {
    if (withPaymentDetail) {
      commands.push(`${formatMessage({ id: 'report.non_cash_payment' })}${LF}`)
      if (edcTotal > 0) {
        commands.push(
          createRowWrapped(
            `  ${formatMessage({ id: 'report.edc_payment' })}`,
            currency(edcTotal),
            width,
          ),
        )
        payments
          .filter((p) => matchType(p.payment_type_id, paymentTypes.EDC, PAYMENT_TYPE.EDC))
          .forEach((p) => {
            commands.push(
              createRowWrapped(
                `    ${p.cashlessPayment?.name || p.payment_type_name || '-'}`,
                currency(p.amount_after_tax || 0),
                width,
              ),
            )
          })
      }
      if (transferTotal > 0) {
        commands.push(
          createRowWrapped(
            `  ${formatMessage({ id: 'report.transfer_payment' })}`,
            currency(transferTotal),
            width,
          ),
        )
        payments
          .filter((p) =>
            matchType(p.payment_type_id, paymentTypes.TRANSFER_BANK, PAYMENT_TYPE.TRANSFER_BANK),
          )
          .forEach((p) => {
            commands.push(
              createRowWrapped(
                `    ${p.cashlessPayment?.name || p.payment_type_name || '-'}`,
                currency(p.amount_after_tax || 0),
                width,
              ),
            )
          })
      }
      if (depositTotal > 0) {
        commands.push(
          createRowWrapped(
            `  ${formatMessage({ id: 'report.deposit_payment' })}`,
            currency(depositTotal),
            width,
          ),
        )
        payments
          .filter((p) => matchType(p.payment_type_id, paymentTypes.DEPOSIT, PAYMENT_TYPE.DEPOSIT))
          .forEach((p) => {
            commands.push(
              createRowWrapped(
                `    ${p.cashlessPayment?.name || p.payment_type_name || '-'}`,
                currency(p.amount_after_tax || 0),
                width,
              ),
            )
          })
      }
      commands.push(border)
      commands.push(
        createRowWrapped(
          `  ${formatMessage({ id: 'report.total_non_cash' })}`,
          currency(cashlessPayment),
          width,
        ),
      )
    } else {
      commands.push(
        createRowWrapped(
          formatMessage({ id: 'report.non_cash_payment' }),
          currency(cashlessPayment),
          width,
        ),
      )
    }
  }
  commands.push(border)
  commands.push(ESCPOS.BOLD_ON)
  commands.push(
    createRowWrapped(formatMessage({ id: 'report.total_payment' }), currency(totalPayment), width),
  )
  commands.push(ESCPOS.BOLD_OFF)

  // ===== 3. Pergerakan Kas (Uang Fisik) =====
  const cashInOuts = Array.isArray(shiftData.cash_in_outs) ? shiftData.cash_in_outs : []
  const cashInDetails = cashInOuts.filter((item) => item?.type === 'in')
  const cashOutDetails = cashInOuts.filter((item) => item?.type === 'out')

  const pushCashRow = (labelId, total, details, { parens } = {}) => {
    commands.push(
      createRowWrapped(formatMessage({ id: labelId }), parens ? `(${total})` : total, width),
    )
    details.forEach((entry) => {
      commands.push(
        createRowWrapped(`- ${entry.memo || entry.desc || '-'}`, currency(entry.amount), width),
      )
      const meta = [
        entry.ref_number,
        entry.trans_date ? formatDate(dayjs(entry.trans_date), 'DD MMM YYYY') : '',
      ]
        .filter(Boolean)
        .join(' · ')
      if (meta) {
        commands.push(`  ${meta}${LF}`)
      }
    })
  }

  sectionTitle(3, 'report.cash_movement_physical')
  commands.push(
    createRowWrapped(
      formatMessage({ id: 'report.initial_capital' }),
      currency(openingBalance),
      width,
    ),
  )
  pushCashRow('report.cash_in', currency(cashIn), cashInDetails)
  commands.push(
    createRowWrapped(formatMessage({ id: 'report.received_cash' }), currency(cashPayment), width),
  )
  pushCashRow('report.cash_out', currency(cashOut), cashOutDetails, { parens: true })
  commands.push(
    createRowWrapped(
      formatMessage({ id: 'report.cash_refund' }),
      `(${currency(cashRefund)})`,
      width,
    ),
  )
  commands.push(border)
  commands.push(ESCPOS.BOLD_ON)
  commands.push(
    createRowWrapped(formatMessage({ id: 'report.cash_in_drawer' }), currency(cashSales), width),
  )
  commands.push(ESCPOS.BOLD_OFF)

  // ===== 4. Rekonsiliasi Kas =====
  sectionTitle(4, 'report.cash_reconciliation')
  commands.push(
    createRowWrapped(
      formatMessage({ id: 'report.actual_cash_counted' }),
      currency(closingBalance),
      width,
    ),
    createRowWrapped(formatMessage({ id: 'report.expected_cash' }), currency(cashSales), width),
  )
  commands.push(border)
  commands.push(ESCPOS.BOLD_ON)
  commands.push(
    createRowWrapped(
      formatMessage({ id: 'report.cash_difference' }),
      `${currency(Math.abs(selisihKas))} (${formatMessage({ id: selisihStatusId })})`,
      width,
    ),
  )
  commands.push(ESCPOS.BOLD_OFF)

  // ===== 5. Produk Terjual (opsional) =====
  if (withProduct && !isEmpty(items)) {
    const rawCategories = Array.isArray(shiftData.product_categories)
      ? shiftData.product_categories
      : []
    const uniqueCategories = Array.from(
      new Map(rawCategories.filter((c) => c && c.id != null).map((c) => [c.id, c])).values(),
    )
    const categoriesWithProducts = uniqueCategories
      .map((category) => {
        const products = items.filter((p) => p.pos_product_category_id === category.id)
        return {
          ...category,
          products,
          totalSoldAmount: products.reduce((s, p) => s + (Number(p?.amount) || 0), 0),
        }
      })
      .filter((c) => !isEmpty(c.products))

    sectionTitle(5, 'report.products_sold')

    if (!isEmpty(categoriesWithProducts)) {
      categoriesWithProducts.forEach((category) => {
        const categoryTotalQty = category.products.reduce((s, p) => s + (Number(p?.qty) || 0), 0)
        commands.push(ESCPOS.BOLD_ON)
        commands.push(`${category.name || '-'} (${categoryTotalQty})${LF}`)
        commands.push(ESCPOS.BOLD_OFF)
        category.products.forEach((item) => {
          const qty = item.qty || 0
          const lineName = item.custom_product_name || item.name || '-'
          commands.push(`- ${lineName} (${qty})${LF}`)
          if (showItemDescription && !isEmpty(item.description)) {
            commands.push(`  > ${item.description}${LF}`)
          }
        })
        commands.push(
          createRowWrapped(
            `  ${formatMessage({ id: 'report.total_sold' })}`,
            currency(category.totalSoldAmount),
            width,
          ),
        )
      })
    } else {
      items.forEach((item) => {
        const qty = item.qty || 0
        const lineName = item.custom_product_name || item.name || '-'
        commands.push(`${lineName} (${qty})${LF}`)
        if (showItemDescription && !isEmpty(item.description)) {
          commands.push(`> ${item.description}${LF}`)
        }
      })
    }

    commands.push(border)
    commands.push(
      createRowWrapped(
        formatMessage({ id: 'report.overall_total' }),
        `${totalProductQty} · ${currency(totalProductGross)}`,
        width,
      ),
    )
  }

  // ===== Footer Summary =====
  commands.push(doubleBorder)
  commands.push(ESCPOS.BOLD_ON)
  commands.push(
    createRowWrapped(formatMessage({ id: 'report.total_revenue' }), currency(totalPayment), width),
  )
  commands.push(ESCPOS.BOLD_OFF)
  commands.push(doubleBorder)

  // Timestamp
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(`--- ${formatDate(dayjs(), 'DD MMM YYYY HH:mm')} ---${LF}`)

  // Feed lines before cut
  commands.push(ESCPOS.FEED_LINES(8))
  if (enableCut) {
    commands.push(ESCPOS.CUT_PAPER_FULL)
  }

  return commands
}

/**
 * Build ESC/POS commands for Labeling
 * Format: (sequence/qty) Product Name, => Contact Name (if exists), Border, Timestamp
 */
export const buildLabelingCommands = (labelData, options = {}) => {
  const {
    paperWidth, // '80mm', '58mm', or number (default: 42)
    enableCut = true, // Enable paper cut at the end (default: true)
  } = options

  // Get paper width (default: 42, or from paperWidth option)
  const width = paperWidth ? getPaperWidth(paperWidth) : PAPER_WIDTHS.DEFAULT

  const commands = [ESCPOS.INIT]

  // Left align for product name and contact
  commands.push(ESCPOS.ALIGN_LEFT)

  // Product name with sequence
  // Format: (sequence/qty) Product Name
  const productLabel = `(${labelData.sequence}/${labelData.qty}) ${labelData.productName || '-'}`
  commands.push(productLabel + LF)

  // Contact name if exists
  if (!isEmpty(labelData.contactName)) {
    commands.push(`=> ${labelData.contactName}${LF}`)
  }

  // Detail meja (order Table) if exists
  if (!isEmpty(labelData.tableLabel)) {
    commands.push(`=> ${labelData.tableLabel}${LF}`)
  }

  // Ekstra (modifier) per label — nama saja
  pushModifierLines(commands, labelData.modifiers, width, { withPrice: false })

  // Border (center aligned)
  commands.push(ESCPOS.ALIGN_CENTER)
  commands.push(createLine('-', width))

  // Timestamp (center aligned)
  // Format: ###DD MMM YYYY HH:mm###
  const timestamp = dayjs().format('DD MMM YYYY HH:mm')
  commands.push(`###${timestamp}###${LF}`)

  // Feed lines before cut
  commands.push(LF)
  commands.push(LF)
  commands.push(LF)

  // Paper cut if enabled
  if (enableCut) {
    commands.push(ESCPOS.CUT_PAPER_FULL)
  }

  return commands
}

/**
 * Convert commands array to byte array
 */
export const commandsToByteArray = (commands) => {
  const str = commandsToString(commands)
  const bytes = []
  for (let i = 0; i < str.length; i += 1) {
    bytes.push(str.charCodeAt(i))
  }
  return bytes
}
